import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Meta from "../../components/common/Meta";
import MainDesign from "../MainDesign";
import {processReferralCode} from "../../util/ReferralUtil";
import {ReferralService} from "../../service/ReferralService";
import AnimatedButton from "../../components/ui/motion/AnimatedButton";
import LoadingComponent from "../../components/common/LoadingComponent";
import {ReferralValidation} from "../../model/user/referrals/Referrals";
import {motion} from "framer-motion";

type ErrorType = 'no_code' | 'invalid_code' | 'own_code' | 'network_error' | null;

export default function ReferralPlatformPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [validation, setValidation] = useState<ReferralValidation | null>(null);
    const [referralCode, setReferralCode] = useState<string | null>(null);
    const [error, setError] = useState<ErrorType>(null);
    const [manualCode, setManualCode] = useState('');
    const [isValidating, setIsValidating] = useState(false);

    function validateCode(code: string) {
        setIsValidating(true);
        setError(null);

        ReferralService.validateReferralCode(code).then(response => {
            setValidation(response);
            setIsValidating(false);
            setLoading(false);
            if (response.valid) {
                setReferralCode(code);
                setError(null);
            } else {
                if (response.message?.includes('own')) {
                    setError('own_code');
                } else {
                    setError('invalid_code');
                }
                setReferralCode(code);
            }
        }).catch(() => {
            setIsValidating(false);
            setLoading(false);
            setError('network_error');
        });
    }

    useEffect(() => {
        const code = processReferralCode();
        if (!code) {
            setError('no_code');
            setLoading(false);
            return;
        }

        setReferralCode(code);
        validateCode(code);
    }, [navigate]);

    function handleManualCodeSubmit(e: React.FormEvent) {
        e.preventDefault();
        const trimmedCode = manualCode.trim().toUpperCase();
        if (trimmedCode.length > 0) {
            validateCode(trimmedCode);
        }
    }

    const handleGetStarted = () => {
        navigate('/rejestracja-saas', {state: {referralCode}});
    };

    if (loading) {
        return (
            <MainDesign containerStyles="container py-md-28 py-16" useGradientBackground minHeight='100vh'>
                <LoadingComponent/>
            </MainDesign>
        );
    }

    if (error || !validation || !validation.valid) {
        const glassCard = {
            background: 'linear-gradient(145deg, rgba(42, 51, 66, 0.35) 0%, rgba(26, 26, 46, 0.55) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.06)',
        };
        const glassCardLight = {
            background: 'linear-gradient(145deg, rgba(42, 51, 66, 0.25) 0%, rgba(26, 26, 46, 0.4) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.04)',
        };

        const errorMessages: Record<string, { title: string; description: string; icon: string; accentColor: string }> = {
            no_code: {
                title: 'Brak kodu polecającego',
                description: 'Nie znaleziono kodu polecającego w linku. Upewnij się, że używasz poprawnego linku od znajomego.',
                icon: 'bi-link-45deg',
                accentColor: '#f59e0c',
            },
            invalid_code: {
                title: 'Nieprawidłowy kod polecający',
                description: 'Kod polecający jest nieprawidłowy lub nieaktywny. Sprawdź, czy link jest poprawny.',
                icon: 'bi-exclamation-triangle',
                accentColor: '#ef4444',
            },
            own_code: {
                title: 'To Twój własny kod',
                description: 'Nie możesz użyć własnego kodu polecającego. Podziel się nim ze znajomymi!',
                icon: 'bi-person-x',
                accentColor: '#f59e0c',
            },
            network_error: {
                title: 'Błąd połączenia',
                description: 'Nie udało się zweryfikować kodu polecającego. Spróbuj ponownie później.',
                icon: 'bi-wifi-off',
                accentColor: '#ef4444',
            }
        };

        const errorInfo = errorMessages[error || 'invalid_code'];

        return (
            <>
                <Meta
                    title="Produkacja OS - Błąd"
                    description="Nie udało się zweryfikować kodu polecającego"
                />
                <MainDesign minHeight='100vh' containerStyles="container py-md-16 py-10" useGradientBackground addOrb>
                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-8 col-xl-6">
                            <motion.div
                                initial={{opacity: 0, y: 20}}
                                animate={{opacity: 1, y: 0}}
                                transition={{duration: 0.5}}
                                className="text-center"
                            >
                                <div className="rounded-4 p-4 p-md-5 mb-4 position-relative overflow-hidden" style={glassCard}>
                                    <div style={{
                                        position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
                                        background: `linear-gradient(90deg, ${errorInfo.accentColor} 0%, transparent 100%)`
                                    }}/>
                                    <div
                                        className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                        style={{
                                            width: '56px', height: '56px',
                                            background: `${errorInfo.accentColor}14`,
                                            border: `1px solid ${errorInfo.accentColor}30`,
                                        }}
                                    >
                                        <i className={`bi ${errorInfo.icon}`} style={{fontSize: '1.4rem', color: errorInfo.accentColor}}></i>
                                    </div>
                                    <h1 className="fs-5 text-white fw-bold mb-3">
                                        {errorInfo.title}
                                    </h1>
                                    <p className="text-secondary-light mb-0" style={{lineHeight: 1.7}}>
                                        {errorInfo.description}
                                    </p>
                                    {referralCode && (
                                        <p className="text-secondary-light mt-3 mb-0">
                                            Kod: <span className="fw-bold" style={{
                                                color: errorInfo.accentColor,
                                                letterSpacing: '0.15em',
                                                fontFamily: '"Courier New", Courier, monospace',
                                            }}>{referralCode}</span>
                                        </p>
                                    )}
                                </div>

                                {/* Manual code input */}
                                <div className="rounded-4 p-4 p-md-5 mb-4" style={glassCard}>
                                    <h6 className="text-white mb-1 fw-semibold" style={{fontSize: '0.95rem'}}>
                                        Masz kod polecający?
                                    </h6>
                                    <p className="text-secondary-light mb-4" style={{fontSize: '0.85rem'}}>
                                        Wpisz go poniżej, aby aktywować zniżkę
                                    </p>
                                    <form onSubmit={handleManualCodeSubmit}>
                                        <div className="d-flex gap-2 justify-content-center">
                                            <input
                                                type="text"
                                                className="form-control text-white text-center text-uppercase"
                                                placeholder="Wpisz kod"
                                                value={manualCode}
                                                onChange={(e) => setManualCode(e.target.value)}
                                                style={{
                                                    maxWidth: '220px',
                                                    letterSpacing: '3px',
                                                    background: 'rgba(255, 255, 255, 0.04)',
                                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                                    borderRadius: '12px',
                                                    padding: '0.6rem 1rem',
                                                }}
                                                disabled={isValidating}
                                            />
                                            <AnimatedButton
                                                type="submit"
                                                className="btn btn-success fw-bold px-4"
                                                style={{borderRadius: '12px'}}
                                                disabled={isValidating || manualCode.trim().length === 0}
                                            >
                                                {isValidating ? (
                                                    <span className="spinner-border spinner-border-sm" role="status"/>
                                                ) : (
                                                    'Sprawdź'
                                                )}
                                            </AnimatedButton>
                                        </div>
                                    </form>
                                </div>

                                <div className="rounded-4 p-4 p-md-5" style={glassCardLight}>
                                    <p className="text-secondary-light mb-3" style={{fontSize: '0.9rem'}}>
                                        Nie masz kodu? Nadal możesz dołączyć do Platformy Edukacyjnej!
                                    </p>
                                    <AnimatedButton
                                        className="btn btn-outline-success fw-bold px-4 py-2"
                                        onClick={() => navigate('/platforma')}
                                        style={{borderRadius: '12px'}}
                                    >
                                        <i className="bi bi-arrow-right-circle me-2"></i>
                                        Przejdź do standardowej oferty
                                    </AnimatedButton>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </MainDesign>
            </>
        );
    }

    const discountPercent = (validation.discount || 0.2) * 100;

    return <>
        <Meta
            title="Produkacja OS - Specjalna Oferta od Znajomego"
            description={`${validation.referrerName || 'Twój znajomy'} zaprasza Cię do Produkacja OS z rabatem ${discountPercent}%!`}
        />
        <MainDesign containerStyles="container py-md-16 py-10" useGradientBackground addOrb>
            <div className="row justify-content-center">
                <div className="col-12 col-lg-10 col-xl-8">

                    {/* Hero Section with Invitation */}
                    <motion.div
                        initial={{opacity: 0, y: 20}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 0.5}}
                        className="text-center mb-4"
                    >
                        <div className="bg-success bg-opacity-10 border border-success rounded-4 p-4 mb-4">
                            <div className="fs-4 mb-2">🚀</div>
                            <p className="text-white mb-1">
                                Twój znajomy {validation.referrerName &&
                                <span className="text-success fw-bold">({validation.referrerName})</span>} zaprasza Cię
                                do
                            </p>
                            <h1 className="fs-5 text-white fw-bold mb-2">
                                Produkacja OS
                            </h1>
                            <p className="text-secondary-light small mb-0">
                                Środowisko do nauki, planowania i działania — wspierane AI
                            </p>
                        </div>
                    </motion.div>

                    {/* Special Discount Card */}
                    <motion.div
                        initial={{opacity: 0, y: 20}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 0.5, delay: 0.1}}
                        className="bg-gradient rounded-4 p-4 mb-4 text-center"
                        style={{background: 'linear-gradient(135deg, #2ad167 0%, #1a8a45 100%)'}}
                    >
                        <div className="mb-2">
                            <span className="badge bg-warning text-dark px-2 py-1 small">
                                Oferta tylko dla poleconych
                            </span>
                        </div>

                        <div className="mb-3">
                            <p className="text-white-50 mb-1 small">Twój rabat na dowolny plan:</p>
                            <span className="fs-2 fw-bold text-warning">
                                -{discountPercent}%
                            </span>
                        </div>

                        <div className="bg-white bg-opacity-10 rounded-3 p-2 mb-3 d-inline-block">
                            <span className="text-white small">
                                <i className="bi bi-piggy-bank me-2"></i>
                                Oszczędzasz nawet <strong className="text-warning">198 zł</strong> rocznie!
                            </span>
                        </div>

                        <div>
                            <AnimatedButton
                                className="btn btn-lg btn-warning fw-bold px-4 py-2"
                                onClick={handleGetStarted}
                            >
                                <i className="bi bi-rocket-takeoff me-2"></i>
                                Załóż konto i odbierz rabat
                            </AnimatedButton>

                            <p className="mt-3 fs-10 text-white-50">
                                Po rejestracji wybierzesz plan i otrzymasz {discountPercent}% zniżki
                            </p>
                        </div>
                    </motion.div>

                    {/* What's Included */}
                    <motion.div
                        initial={{opacity: 0, y: 20}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 0.5, delay: 0.2}}
                        className="bg-dark rounded-4 p-4 mb-4"
                    >
                        <h5 className="text-white mb-3 text-center">
                            <i className="bi bi-box-seam text-success me-2"></i>
                            Co otrzymujesz w planie Pro?
                        </h5>

                        <div className="row g-3">
                            <div className="col-md-6">
                                <div className="d-flex align-items-start">
                                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                                    <div>
                                        <p className="text-white mb-0 small">Pełny dostęp do platformy</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex align-items-start">
                                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                                    <div>
                                        <p className="text-white mb-0 small">30 planów nauki miesięcznie</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex align-items-start">
                                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                                    <div>
                                        <p className="text-white mb-0 small">Nieograniczone fiszki</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex align-items-start">
                                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                                    <div>
                                        <p className="text-white mb-0 small">Plan Dominacji</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex align-items-start">
                                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                                    <div>
                                        <p className="text-white mb-0 small">Kurs "Podstawy Efektywnego Działania"</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex align-items-start">
                                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                                    <div>
                                        <p className="text-white mb-0 small">Priorytetowe wsparcie</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Testimonials */}
                    <motion.div
                        initial={{opacity: 0, y: 20}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 0.5, delay: 0.3}}
                        className="bg-dark rounded-4 p-4 mb-4"
                    >
                        <h5 className="text-white mb-3 text-center">
                            <i className="bi bi-chat-quote text-warning me-2"></i>
                            Co mówią użytkownicy?
                        </h5>

                        <div className="row g-3">
                            <div className="col-md-4">
                                <div className="bg-secondary bg-opacity-25 rounded-3 p-3 h-100">
                                    <div className="text-warning small mb-1">⭐⭐⭐⭐⭐</div>
                                    <p className="text-white small mb-2">
                                        "Bardzo podoba mi się sekcja z zadaniami - możliwość wpisania odpowiedzi,
                                        rysowania i szybka podpowiedź. To jedna z najlepszych części platformy."
                                    </p>
                                    <p className="text-secondary-light small mb-0">— Julia, 3 klasa liceum</p>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="bg-secondary bg-opacity-25 rounded-3 p-3 h-100">
                                    <div className="text-warning small mb-1">⭐⭐⭐⭐⭐</div>
                                    <p className="text-white small mb-2">
                                        "Są super i wszystko działa jak trzeba. XP jest dodawane dobrze, a generowane
                                        odpowiedzi są mega."
                                    </p>
                                    <p className="text-secondary-light small mb-0">— Oliwier, 8 klasa</p>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="bg-secondary bg-opacity-25 rounded-3 p-3 h-100">
                                    <div className="text-warning small mb-1">⭐⭐⭐⭐⭐</div>
                                    <p className="text-white small mb-2">
                                        "Testuję zadania w wersji beta i bardzo dobrze się sprawdzają. Korzystałbym.
                                        Bardzo duży wybór i wszystko się przydaje."
                                    </p>
                                    <p className="text-secondary-light small mb-0">— Krzysiek, 2 klasa liceum</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Program Rules Info */}
                    <motion.div
                        initial={{opacity: 0, y: 20}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 0.5, delay: 0.4}}
                        className="bg-warning bg-opacity-10 border border-warning rounded-4 p-3 mb-4"
                    >
                        <h6 className="text-warning mb-2">
                            <i className="bi bi-info-circle me-2"></i>
                            Ważne informacje
                        </h6>
                        <ul className="text-secondary-light mb-0 ps-3 small">
                            <li className="mb-1">Ta specjalna oferta jest dostępna tylko przez link polecający</li>
                            <li className="mb-1">Możesz przetestować działanie platformy przed dokonaniem płatności</li>
                            <li className="mb-1">Pełny dostęp do platformy otrzymujesz natychmiast po płatności</li>
                            <li className="mb-1">Możesz anulować subskrypcję w dowolnym momencie</li>
                            <li className="mb-0">Możesz również polecać platformę innym i zarabiać prowizję</li>
                        </ul>
                    </motion.div>

                    {/* Final CTA */}
                    <motion.div
                        initial={{opacity: 0, y: 20}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 0.5, delay: 0.5}}
                        className="text-center"
                    >
                        <p className="text-secondary-light mb-3 small">
                            Nie przegap tej okazji - oferta dostępna tylko przez link polecający
                        </p>
                        <AnimatedButton
                            className="btn btn-lg btn-success fw-bold px-4 py-2"
                            onClick={handleGetStarted}
                        >
                            <i className="bi bi-arrow-right-circle me-2"></i>
                            Załóż konto i odbierz -{discountPercent}%
                        </AnimatedButton>
                        <p className="text-secondary-light mt-2 small">
                            Kod polecający: <span className="text-success fw-bold">{referralCode}</span>
                        </p>
                    </motion.div>
                </div>
            </div>
        </MainDesign>
    </>;
}
